import { GET_SKU_URL, UPDATE_SKU_URL } from '@constants';
import { SKUForm } from '@forms/SKUForm/SKUForm';
import { ProductSkusFormValues, SelectedSKU } from '@forms/SKUForm/types';
import { flattenCard, regroupToFormShape } from '@forms/SKUForm/payload';
import { useAuthHttp } from '@hooks';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import {
  stripLabelValue,
  buildSkuApiPayload,
  mapSkuToFormValues,
} from '../../utils/sku';

export function UpdateSKU() {
  useDocumentTitle('FLOX - Update SKU');
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingSKU, setIsFetchingSKU] = useState(false);
  const [sku, setSku] = useState<SelectedSKU>();
  const { id } = useParams<{ id: string }>();

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Products', href: '/sku-master', current: false },
    { name: 'Update SKU', href: '/sku/update', current: true },
  ];

  const fetchSKU = async (isStillOnThisSku: () => boolean) => {
    setIsFetchingSKU(true);
    try {
      const { data }: AxiosResponse<SelectedSKU> = await authHttp.get(
        GET_SKU_URL,
        {
          params: {
            skuId: id,
          },
        }
      );
      if (isStillOnThisSku()) setSku(data?.sku ? data : undefined);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      if (isStillOnThisSku()) setIsFetchingSKU(false);
    }
  };

  const handleOnSubmit = async (data: ProductSkusFormValues) => {
    setIsSubmitting(true);

    const flat = flattenCard(data, 0);
    const cleanedData = stripLabelValue(flat);
    const payload = buildSkuApiPayload(flat, cleanedData, { id });

    try {
      const { data: updateSKUData } = await authHttpFormData.post(
        UPDATE_SKU_URL,
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (updateSKUData) {
        navigate('/sku-master', { replace: true });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let isStillOnThisSku = true;
    setSku(undefined);
    fetchSKU(() => isStillOnThisSku);
    return () => {
      isStillOnThisSku = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isFetchingSKU) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <FloxSpinner size='lg' />
      </div>
    );
  }

  if (!sku) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <span className='text-gray-700 text-lg'>No SKU found</span>
      </div>
    );
  }

  return (
    <SKUForm
      key={sku.sku.inventorySkuId}
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      pages={pages}
      header='Update SKU'
      initialValues={regroupToFormShape(mapSkuToFormValues(sku))}
      mode='edit'
    />
  );
}
